import { MARKET_CODES } from "../../../../../common/enums/market-enums";
import type { AboKpiData } from "../../../../../services/AboEnrichment/AboEnrichmentAPI.types";
import type { ActionReportRow } from "../types";

/**
 * Generates the affAboId from a row
 * @param row - The action report row containing aff and aboNumber
 * @returns The formatted string `{aff}-{aboNumber}` or null if aff is not present
 */
export const getAffAboId = (row: ActionReportRow): string | null => {
  return row.aff ? `${row.aff}-${row.aboNumber}` : null;
};

export const isChinaAff = (aff: string): boolean => aff === MARKET_CODES.CN;

/**
 * Extra breathing room (in px, ~0.5rem at the default root font size) kept between
 * the sticky nav's bottom edge and the scrolled-to row's top edge, so the row
 * doesn't land flush against the nav.
 */
const NAV_CLEARANCE_PX = 8;

/**
 * A single deferred measurement isn't enough on real iOS Safari (not the
 * Simulator): the address bar's collapse/expand animation and momentum-scroll
 * settle can keep shifting the layout for several hundred ms after
 * scrollIntoView, so one measurement — however delayed — can still land mid-shift
 * and under-correct. Re-check/re-correct on this schedule instead of once,
 * mirroring useVirtuosoScrollToIndex's retry window.
 */
const NAV_RETRY_DELAYS_MS = [0, 100, 250, 450, 700];

/**
 * scrollIntoView doesn't know about sticky/fixed overlays — a row can pass its
 * visibility check while sitting behind the sticky nav (desktop's TopNavigationBar
 * or mobile's MobileNavigationBar — only one renders at a time). Nudge the window
 * down by however much the nav actually covers the row's top edge, plus a small
 * clearance gap (varies with the impersonation banner and responsive breakpoints,
 * so this is measured, not a fixed CSS offset).
 */
export const scrollClearOfStickyNav = (row: HTMLElement): void => {
  const correct = (): void => {
    if (!row.isConnected) return;
    const nav = document.querySelector<HTMLElement>(".TopNavigationBar, .MobileNavigationBar");
    if (!nav) return;
    const overlap = nav.getBoundingClientRect().bottom - row.getBoundingClientRect().top + NAV_CLEARANCE_PX;
    if (overlap > 0) {
      // Scrolling the window up (negative) moves the row's top edge down on screen,
      // out from behind the nav — scrolling down would push it further underneath.
      window.scrollBy({ top: -overlap, behavior: "instant" });
    }
  };
  NAV_RETRY_DELAYS_MS.forEach((delay) => setTimeout(correct, delay));
};

export const normalizeEnrichedData = (data: AboKpiData[]): AboKpiData[] =>
  data.map((record) => {
    const { ppvARRequirement, ...rest } = record as AboKpiData & {
      ppvARRequirement?: AboKpiData["personalPVRequirement"];
    };
    if (ppvARRequirement !== undefined) {
      return { ...rest, personalPVRequirement: ppvARRequirement };
    }
    return record;
  });
